import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import * as S from './AdLayoutWatchdogStyles';
import { COMMON_TEST_IDS } from 'constants/testsIds/common';
import logAdCLSError from 'utils/log/logAdCLSError';
import { isMobileDevice } from 'utils/miscUtils/isMobileDevice';

const CLS_WATCHDOG_THRESHOLD_PX = parseInt(process.env.CLS_WATCHDOG_THRESHOLD_PX);
const CLS_WATCHDOG_MILLIS = parseInt(process.env.CLS_WATCHDOG_MILLIS);

const INTERACTION_EVENTS: Array<keyof GlobalEventHandlersEventMap> = [
  'click',
  'scroll',
  'resize',
  'keydown',
  'touchstart',
];

export interface AdLayoutWatchdogProps extends React.PropsWithChildren {
  matchAdUnits: string[];
}

type AdRenderEventMatch = {
  adUnitPath: string;
  creativeId: string;
  lineItemId: string;
};

export const AdLayoutWatchdog = ({ matchAdUnits }: AdLayoutWatchdogProps) => {
  const interactionListenersMounted = useRef(false);
  const detectedFirstUserInteraction = useRef(false);

  const capturedAdRenderEvent = useRef<AdRenderEventMatch | null>(null);

  const clsTrackingInitialValue = useRef<number | null>(null);
  const clsTrackingRef = useRef<HTMLDivElement>(null);

  const [freestarLoaded, setFreestarLoaded] = useState(false);
  const [listenerEnqueued, setListenerEnqueued] = useState(false);

  // Wait for Freestar to load
  // This might be redundant, as `window.freestar.queue` is technically available from the start, but it's best to be safe
  useEffect(() => {
    const intvl = setInterval(() => {
      if (window.freestar?.queue) {
        setFreestarLoaded(true);
        clearInterval(intvl);
      }
    }, 25);

    return () => clearInterval(intvl);
  }, []);

  // Mount Ad Rendered event listener
  useEffect(() => {
    if (!freestarLoaded) return;
    if (listenerEnqueued) return;

    window.freestar.queue.push(() => {
      const pubads = window.googletag.pubads();

      pubads.addEventListener('slotRenderEnded', (event) => {
        const adUnitPath = event.slot.getAdUnitPath();
        const creativeId = event.sourceAgnosticCreativeId;
        const lineItemId = event.sourceAgnosticLineItemId;

        // These are null if we can't display ads (i.e. consent rejected)
        // One would think they just wouldn't fire - I thought the same
        // They fire, but the data is null
        // We just drop out if that's the case
        if (creativeId === null && lineItemId === null) return;

        // Check if the Ad Unit ID matches what we're looking for
        const matches = matchAdUnits.some((term) => adUnitPath.includes(term));
        if (matches) {
          // Store the data into a ref, will be used for reporting in case CLS is detected under it
          capturedAdRenderEvent.current = {
            adUnitPath,
            creativeId: creativeId.toString(),
            lineItemId: lineItemId.toString(),
          };
        }
      });
    });

    setListenerEnqueued(true);
  }, [freestarLoaded, listenerEnqueued]);

  // Mount CLS watchdog
  useEffect(() => {
    const intvl = setInterval(() => {
      // This should theoretically never happen (since the interval is cleared by the handler), but it's left here Just-In-Case (TM)
      // Break out of the function if the first interaction has already been detected
      if (detectedFirstUserInteraction.current) return;

      const top = clsTrackingRef.current?.getBoundingClientRect().top;

      if (top !== undefined) {
        if (clsTrackingInitialValue.current === null) {
          // Set the initial value
          clsTrackingInitialValue.current = top;
        } else {
          // Check for y-offset differences
          // If the difference is higher than the threshold, report it and unmount listeners
          const delta = Math.abs(top - clsTrackingInitialValue.current);
          if (delta > CLS_WATCHDOG_THRESHOLD_PX) {
            if (capturedAdRenderEvent.current !== null) {
              console.log(`[ALW] CLS: ${delta}px`);
              console.log(capturedAdRenderEvent.current);

              const { adUnitPath, creativeId, lineItemId } = capturedAdRenderEvent.current;
              logAdCLSError(
                window.location.pathname,
                isMobileDevice() ? 'mobile' : 'desktop',
                window.innerWidth,
                delta,
                adUnitPath,
                creativeId,
                lineItemId,
              );

              removeCLSWatchdog();
            }
          }
        }
      }
    }, CLS_WATCHDOG_MILLIS);

    // Cleanup function to stop the CLS Watchdog
    const removeCLSWatchdog = () => {
      if (!interactionListenersMounted.current) return;
      clearInterval(intvl);
      INTERACTION_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleFirstInteraction);
      });
      interactionListenersMounted.current = false;
    };

    // First interaction handler
    // Unmounts the CLS Watchdog; We don't want to track y-offsets if the user interacted
    const handleFirstInteraction = () => {
      if (detectedFirstUserInteraction.current) return;
      detectedFirstUserInteraction.current = true;
      console.log(`[ALW] First interaction detected`);
      removeCLSWatchdog();
    };

    INTERACTION_EVENTS.forEach((event) => {
      window.addEventListener(event, handleFirstInteraction);
    });
    interactionListenersMounted.current = true;

    return removeCLSWatchdog;
  }, []);

  return (
    <S.AdLayoutSensor
      aria-hidden
      data-cy={COMMON_TEST_IDS.AD_LAYOUT_WATCHDOG}
      ref={clsTrackingRef}
    />
  );
};
